import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiX, FiUserPlus, FiTrash2 } from 'react-icons/fi';
import { useConfirm } from '../ui/ConfirmDialog';

/**
 * Share a Planwise board with other people by email.
 *
 * Backend routes used:
 *   GET    /api/planwise/boards/:id/collaborators
 *   POST   /api/planwise/boards/:id/collaborators
 *   DELETE /api/planwise/boards/:id/collaborators/:collaboratorId
 *
 * Permission: only the board owner and existing admin collaborators can
 * open this modal productively. The backend enforces this independently.
 *
 * Props:
 *   boardId  - UUID of the board being shared
 *   onClose  - close callback
 */
const ShareBoardModal = ({ boardId, onClose }) => {
  const confirm = useConfirm();
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState('view');

  const fetchCollaborators = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/planwise/boards/${boardId}/collaborators`);
      setCollaborators(r.data || []);
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to load collaborators';
      toast.error(msg);
      setCollaborators([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (boardId) fetchCollaborators();
  }, [boardId]);

  const addCollaborator = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`/api/planwise/boards/${boardId}/collaborators`, {
        email: email.trim(),
        permission_level: permission
      });
      setEmail('');
      setPermission('view');
      toast.success('Collaborator added');
      await fetchCollaborators();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add collaborator');
    } finally {
      setSaving(false);
    }
  };

  const removeCollaborator = async (collaboratorId) => {
    const ok = await confirm({
      title: 'Remove this collaborator?',
      description: 'They will lose access to the board.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/planwise/boards/${boardId}/collaborators/${collaboratorId}`);
      setCollaborators(prev => prev.filter(c => c.id !== collaboratorId));
      toast.success('Collaborator removed');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to remove collaborator');
    }
  };

  const labelFor = (c) => {
    if (c.user) return `${c.user.firstName || ''} ${c.user.lastName || ''}`.trim() || c.user.email;
    return c.email || '(unknown)';
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-gray-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Share Board</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <form onSubmit={addCollaborator} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Invite by email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="colleague@company.com"
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Permission</label>
              <select
                value={permission}
                onChange={e => setPermission(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="view">View — can see tasks and comments</option>
                <option value="edit">Edit — can create and modify tasks</option>
                <option value="admin">Admin — can edit and share the board</option>
              </select>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-md transition-colors"
              >
                <FiUserPlus className="h-4 w-4" />
                {saving ? 'Adding…' : 'Add'}
              </button>
            </div>
          </form>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              People with access ({collaborators.length})
            </h4>
            {loading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
            ) : collaborators.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No one else has been added yet.</p>
            ) : (
              <ul className="space-y-2">
                {collaborators.map(c => (
                  <li key={c.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded">
                    <div>
                      <div className="text-sm text-gray-900 dark:text-white">{labelFor(c)}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{c.email} · {c.permission_level}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCollaborator(c.id)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400"
                      aria-label={`Remove ${labelFor(c)}`}
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareBoardModal;
